import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTheme } from "../../context/ThemeContext";
import { apiClient } from "../../lib/api";
import { CONTACT_INFO } from "../../lib/siteContent";

const quickLinks = [
  { label: "Home", to: "/" },
  { label: "Shop the Drop", to: "/shop" },
  { label: "Acid Wash Premium", to: "/collections/acid-wash-premium" },
  { label: "Customize Your Drip", to: "/customize" },
  { label: "About", to: "/about" },
  { label: "Contact Us", to: "/contact" },
];

const policyLinks = [
  { label: "Shipping & Delivery Policy", to: "/policies/shipping" },
  { label: "Return & Exchange (7 Days)", to: "/policies/returns" },
  { label: "Privacy & Data Policy", to: "/policies/privacy" },
  { label: "Terms of Service", to: "/policies/terms" },
  { label: "Size Guide", to: "/size-guide" },
];

export const Footer = () => {
  const { isDark } = useTheme();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [newsletterNotice, setNewsletterNotice] = useState({ type: "", message: "" });

  const submitNewsletter = async (event) => {
    event.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setNewsletterNotice({ type: "error", message: "Please enter a valid email." });
      return;
    }

    setSubmitting(true);
    setNewsletterNotice({ type: "", message: "" });
    try {
      await apiClient.post("/leads", { email: cleanEmail, source: "footer-newsletter" });
      toast.success("Thanks for joining Eternal Order updates.");
      setEmail("");
      setNewsletterNotice({ type: "success", message: "You're on the list. We'll notify you about new drops." });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const message = typeof detail === "string" ? detail : "Unable to submit right now. Please try again.";
      toast.error(message);
      setNewsletterNotice({ type: "error", message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="border-t border-border bg-card/40">
      <div className="mx-auto grid w-full max-w-[1400px] gap-8 px-4 py-14 sm:px-6 md:grid-cols-2 xl:grid-cols-4 lg:px-8">
        <div className="space-y-4">
          <p className="brand-logo text-xs tracking-[0.35em] text-[#C2A45D]" data-testid="footer-brand-title">
            ETERNAL ORDER
          </p>
          <p className="text-sm text-muted-foreground" data-testid="footer-brand-statement">
            Identity-driven apparel inspired by Sanatana philosophy.
          </p>
        </div>

        <div>
          <p className="mb-3 text-xs uppercase tracking-[0.2em] text-[#C2A45D]">Quick Links</p>
          <div className="space-y-2 text-sm text-foreground/80">
            {quickLinks.map((item) => (
              <Link key={item.to} to={item.to} className="block hover:text-[#C2A45D]" data-testid={`footer-quick-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        <div>
          <p className="mb-3 text-xs uppercase tracking-[0.2em] text-[#C2A45D]">Policies</p>
          <div className="space-y-2 text-sm text-foreground/80">
            {policyLinks.map((item) => (
              <Link key={item.to} to={item.to} className="block hover:text-[#C2A45D]" data-testid={`footer-policy-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C2A45D]">Contact</p>
          <a href={`mailto:${CONTACT_INFO.email}`} className="block break-all text-sm text-foreground/80 hover:text-[#C2A45D]" data-testid="footer-contact-email-link">
            {CONTACT_INFO.email}
          </a>
          <a href={`tel:+91${CONTACT_INFO.phone}`} className="block text-sm text-foreground/80 hover:text-[#C2A45D]" data-testid="footer-contact-phone-link">
            +91 {CONTACT_INFO.phone}
          </a>
          <a href={`https://instagram.com/${CONTACT_INFO.instagram}`} className="block text-sm text-foreground/80 hover:text-[#C2A45D]" data-testid="footer-contact-instagram-link">
            Instagram: @{CONTACT_INFO.instagram}
          </a>
          <p className="text-sm text-foreground/80" data-testid="footer-contact-facebook-text">
            Facebook: {CONTACT_INFO.facebook}
          </p>

          <form onSubmit={submitNewsletter} className="space-y-2" data-testid="footer-newsletter-form">
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Enter your email"
              className="w-full rounded-full border border-border bg-background px-4 py-2 text-sm text-foreground outline-none focus:border-[#C2A45D]"
              required
              autoComplete="email"
              data-testid="footer-newsletter-input"
            />
            <button
              type="submit"
              disabled={submitting}
              className={`w-full rounded-full border px-4 py-2 text-sm font-medium transition ${
                isDark
                  ? "border-[#C2A45D] bg-[#05070D] text-[rgb(245,245,245)] hover:bg-[#C2A45D] hover:text-[#1C1C1C]"
                  : "border-[#C6A85A] bg-[#0b1220] text-[rgb(255,255,255)] hover:bg-[#18233b]"
              } disabled:cursor-not-allowed disabled:opacity-65`}
              data-testid="footer-newsletter-submit-button"
            >
              {submitting ? "Joining..." : "Join Updates"}
            </button>
            {newsletterNotice.message ? (
              <p
                className={`text-xs ${newsletterNotice.type === "success" ? "text-emerald-300" : "text-rose-300"}`}
                data-testid="footer-newsletter-status-message"
              >
                {newsletterNotice.message}
              </p>
            ) : null}
          </form>
        </div>
      </div>

      <div className="border-t border-border py-4 text-center text-xs text-muted-foreground" data-testid="footer-copyright">
        © {new Date().getFullYear()} Eternal Order. All rights reserved.
      </div>
    </footer>
  );
};
